// src/pages/CampaignManager.jsx
//
// Advanced WhatsApp campaign manager: recipients, template parameters,
// schedule, run control and history for a single campaign. Backed by
// routers/campaign_scheduler.py.
//
// `CampaignDetail` is the whole manager for one campaign and is mounted both by
// this standalone page and by the WhatsApp Ads page, so there is one
// implementation of the controls rather than two that drift apart.
//
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FiUpload, FiPlay, FiPause, FiClock, FiCalendar, FiRefreshCw, FiTrash2,
  FiAlertTriangle, FiCheckCircle, FiChevronLeft, FiUsers, FiZap, FiSliders,
  FiActivity, FiSave,
} from "react-icons/fi";

// Relative API base on purpose: an absolute https://maa.ycpay.in/api only works
// on production and forces a cross-origin request even when the dashboard is
// served from the same host.
const API = "/api";

export const DAYS = [
  { n: 1, s: "Mon", long: "Monday" }, { n: 2, s: "Tue", long: "Tuesday" },
  { n: 3, s: "Wed", long: "Wednesday" }, { n: 4, s: "Thu", long: "Thursday" },
  { n: 5, s: "Fri", long: "Friday" }, { n: 6, s: "Sat", long: "Saturday" },
  { n: 7, s: "Sun", long: "Sunday" },
];

export const TIMEZONES = [
  "Asia/Kolkata", "Asia/Dubai", "Asia/Colombo", "Africa/Cairo", "Africa/Nairobi",
  "UTC", "Europe/London", "America/New_York",
];

const STATUS_STYLE = {
  ACTIVE:    "bg-green-100 text-green-800 ring-green-200",
  PAUSED:    "bg-amber-100 text-amber-800 ring-amber-200",
  DRAFT:     "bg-gray-100 text-gray-700 ring-gray-200",
  COMPLETED: "bg-brand-100 text-brand-800 ring-brand-200",
  ENDED:     "bg-gray-100 text-gray-600 ring-gray-200",
  DELETED:   "bg-red-100 text-red-700 ring-red-200",
};

export const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

// A datetime-local input needs local wall-clock text, not a UTC ISO string —
// slicing the ISO directly shifts every time by the browser's offset.
export const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
         `T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export async function call(path, options = {}) {
  const res = await fetch(`${API}${path}`, {
    credentials: "include",
    headers: options.body instanceof FormData ? {} : { "Content-Type": "application/json" },
    ...options,
  });
  const text = await res.text();
  let body;
  try { body = text ? JSON.parse(text) : {}; } catch { body = { detail: text }; }
  if (!res.ok) {
    const detail = body?.detail || body?.message || `HTTP ${res.status}`;
    throw new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
  }
  return body;
}

export function Pill({ status }) {
  const cls = STATUS_STYLE[status] || STATUS_STYLE.DRAFT;
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${cls}`}>
      {status}
    </span>
  );
}

export function Banner({ kind = "info", children, onClose }) {
  if (!children) return null;
  const map = {
    error: "border-red-200 bg-red-50 text-red-800",
    ok: "border-green-200 bg-green-50 text-green-800",
    warn: "border-amber-200 bg-amber-50 text-amber-800",
    info: "border-brand-200 bg-brand-50 text-brand-800",
  };
  const Icon = kind === "ok" ? FiCheckCircle : FiAlertTriangle;
  return (
    <div className={`flex items-start gap-2 rounded-xl border px-3 py-2 text-sm ${map[kind]}`}>
      <Icon className="mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1 break-words">{children}</div>
      {onClose && (
        <button onClick={onClose} className="shrink-0 px-1 font-bold opacity-60 hover:opacity-100">×</button>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   Diagnostics — turns a blank 500 into a named cause
   ═════════════════════════════════════════════════════════════════════════ */
function Diagnostics() {
  const [checks, setChecks] = useState(null);
  const [open, setOpen] = useState(false);

  const run = async () => {
    setOpen(true);
    try {
      const out = await call("/campaign-scheduler/diagnostics");
      setChecks(out.checks || []);
    } catch (e) {
      setChecks([{ check: "diagnostics endpoint", ok: false, detail: e.message }]);
    }
  };

  return (
    <div className="mt-2">
      <button onClick={run} className="text-xs font-semibold text-[#F96168] underline">
        Run diagnostics
      </button>
      {open && (
        <div className="mt-2 space-y-1 rounded-xl border border-gray-200 bg-gray-50 p-3 text-xs">
          {!checks && <p className="text-gray-500">Checking…</p>}
          {checks?.map((c) => (
            <div key={c.check} className="flex gap-2">
              <span className={c.ok ? "text-green-600" : "text-red-600"}>{c.ok ? "✓" : "✗"}</span>
              <span className="font-semibold text-gray-800">{c.check}:</span>
              <span className="min-w-0 flex-1 break-words text-gray-600">{c.detail}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   Schedule editor
   ═════════════════════════════════════════════════════════════════════════ */
export function ScheduleEditor({ campaign, onSaved, onError }) {
  const s = campaign.schedule || {};
  const [type, setType] = useState(s.type || "once");
  const [days, setDays] = useState(s.days_of_week || []);
  const [time, setTime] = useState(s.send_time || "10:00");
  const [tz, setTz] = useState(s.timezone || "Asia/Kolkata");
  const [mode, setMode] = useState(s.trigger_mode || "scheduled");
  const [start, setStart] = useState(toLocalInput(campaign.start_date));
  const [end, setEnd] = useState(toLocalInput(campaign.end_date));
  const [saving, setSaving] = useState(false);

  const toggleDay = (n) =>
    setDays((d) => (d.includes(n) ? d.filter((x) => x !== n) : [...d, n].sort((a, b) => a - b)));

  const problem = useMemo(() => {
    if (type === "weekly" && days.length === 0) return "Pick at least one day of the week.";
    if (type === "once" && !start) return "A one-shot campaign needs a start date and time.";
    if (type !== "once" && (!start || !end)) return "Recurring campaigns need both a start and an end date.";
    if (start && end && new Date(end) < new Date(start)) return "The end date is before the start date.";
    return null;
  }, [type, days, start, end]);

  // The schedule window is a plain date range; the time of day comes from
  // `send_time` in the campaign's own timezone, so a recurring campaign's
  // start/end only need to bracket the days it may run.
  const preview = useMemo(() => {
    if (type === "once") return start ? `Once, on ${new Date(start).toLocaleString()}` : "—";
    if (type === "daily") return `Every day at ${time} (${tz})`;
    if (!days.length) return "Weekly — no days selected, it will never run";
    const names = days.map((n) => DAYS.find((d) => d.n === n)?.long).filter(Boolean);
    return `Every ${names.join(", ")} at ${time} (${tz})`;
  }, [type, days, time, tz, start]);

  const save = async () => {
    if (problem) return;
    setSaving(true);
    try {
      const out = await call(`/campaigns/${campaign.campaign_id}/schedule`, {
        method: "PUT",
        body: JSON.stringify({
          schedule_type: type,
          days_of_week: type === "weekly" ? days : [],
          send_time: time,
          timezone: tz,
          trigger_mode: mode,
          start_date: start ? new Date(start).toISOString() : null,
          end_date: end ? new Date(end).toISOString() : null,
        }),
      });
      onSaved(out);
    } catch (e) { onError(e.message); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div>
        <div className="mb-2 text-sm font-semibold text-gray-700">Repeat</div>
        <div className="grid grid-cols-3 gap-2">
          {[
            { v: "once", l: "One-shot", d: "Send once" },
            { v: "daily", l: "Daily", d: "Every day" },
            { v: "weekly", l: "Weekly", d: "Chosen days" },
          ].map((o) => (
            <button
              key={o.v}
              onClick={() => setType(o.v)}
              className={`rounded-xl border px-3 py-3 text-left transition ${
                type === o.v ? "border-[#F96168] bg-[#F96168]/10 ring-1 ring-[#F96168]"
                             : "border-gray-200 hover:bg-gray-50"}`}
            >
              <div className="text-sm font-semibold text-gray-900">{o.l}</div>
              <div className="text-xs text-gray-500">{o.d}</div>
            </button>
          ))}
        </div>
      </div>

      {type === "weekly" && (
        <div>
          <div className="mb-2 text-sm font-semibold text-gray-700">Days</div>
          <div className="flex flex-wrap gap-2">
            {DAYS.map((d) => (
              <button
                key={d.n}
                onClick={() => toggleDay(d.n)}
                className={`min-h-[44px] min-w-[52px] rounded-xl border px-3 text-sm font-semibold transition ${
                  days.includes(d.n) ? "border-[#F96168] bg-[#F96168] text-black"
                                     : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
              >
                {d.s}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        {type !== "once" && (
          <label className="block">
            <span className="mb-1 block text-sm font-semibold text-gray-700">Send at</span>
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)}
                   className="w-full rounded-xl border border-gray-300 px-3 py-2.5" />
          </label>
        )}
        <label className="block">
          <span className="mb-1 block text-sm font-semibold text-gray-700">Timezone</span>
          <select value={tz} onChange={(e) => setTz(e.target.value)}
                  className="w-full rounded-xl border border-gray-300 px-3 py-2.5">
            {TIMEZONES.map((z) => <option key={z} value={z}>{z}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-semibold text-gray-700">
            {type === "once" ? "Send on" : "Starts"}
          </span>
          <input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)}
                 className="w-full rounded-xl border border-gray-300 px-3 py-2.5" />
        </label>
        {type !== "once" && (
          <label className="block">
            <span className="mb-1 block text-sm font-semibold text-gray-700">Ends</span>
            <input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)}
                   className="w-full rounded-xl border border-gray-300 px-3 py-2.5" />
          </label>
        )}
      </div>

      <div>
        <div className="mb-2 text-sm font-semibold text-gray-700">When recipients are uploaded</div>
        <div className="grid grid-cols-2 gap-2">
          {[
            { v: "scheduled", l: "Wait for schedule", d: "Send at the next slot" },
            { v: "immediate", l: "Send immediately", d: "Fire as soon as the list lands" },
          ].map((o) => (
            <button key={o.v} onClick={() => setMode(o.v)}
              className={`rounded-xl border px-3 py-3 text-left transition ${
                mode === o.v ? "border-[#F96168] bg-[#F96168]/10 ring-1 ring-[#F96168]"
                             : "border-gray-200 hover:bg-gray-50"}`}>
              <div className="text-sm font-semibold text-gray-900">{o.l}</div>
              <div className="text-xs text-gray-500">{o.d}</div>
            </button>
          ))}
        </div>
      </div>

      <Banner kind="info">{preview}</Banner>
      {problem && <Banner kind="warn">{problem}</Banner>}

      <button onClick={save} disabled={saving || !!problem}
        className="min-h-[44px] w-full rounded-xl bg-[#F96168] px-4 py-3 font-semibold text-black disabled:opacity-50">
        {saving ? "Saving…" : "Save schedule"}
      </button>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   Template parameters
   ═════════════════════════════════════════════════════════════════════════ */
function TemplateParams({ campaign, onSaved, onError }) {
  const [spec, setSpec] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const out = await call(`/campaigns/${campaign.campaign_id}/template-spec`);
        if (!alive) return;
        setSpec(out.template);
        setValues(out.defaults || {});
        setNote(out.message || "");
      } catch (e) { if (alive) onError(e.message); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [campaign.campaign_id, onError]);

  const save = async () => {
    setSaving(true);
    try {
      const cleaned = Object.fromEntries(
        Object.entries(values).filter(([, v]) => String(v || "").trim() !== "")
      );
      const out = await call(`/campaigns/${campaign.campaign_id}`, {
        method: "PATCH", body: JSON.stringify({ default_params: cleaned }),
      });
      onSaved(out);
    } catch (e) { onError(e.message); } finally { setSaving(false); }
  };

  // Substituting the values into the real body text is the only reliable way to
  // see that {{1}} and {{2}} are the right way round before sending.
  const rendered = useMemo(() => {
    if (!spec?.body_text) return "";
    return spec.body_text.replace(/\{\{\s*(\d+)\s*\}\}/g,
      (m, n) => values[n] || `«Param ${n}»`);
  }, [spec, values]);

  if (loading) return <p className="text-sm text-gray-500">Reading the template…</p>;
  if (!spec) return <p className="text-sm text-gray-500">No template information.</p>;

  return (
    <div className="space-y-4">
      {note && <Banner kind="warn">{note}</Banner>}

      <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
        <div className="text-xs uppercase text-gray-500">Template</div>
        <div className="font-mono text-sm font-semibold text-gray-900">{spec.name}</div>
        <div className="mt-1 text-xs text-gray-500">
          {[spec.category, spec.language, spec.status].filter(Boolean).join(" · ")}
        </div>
      </div>

      {spec.param_count === 0 ? (
        <Banner kind="ok">This template has no variables — nothing to fill in.</Banner>
      ) : (
        <>
          <p className="text-sm text-gray-600">
            Default values are used for any recipient whose row does not supply that
            parameter. A <code>Param&nbsp;{"N"}</code> column in the uploaded file always wins.
          </p>
          <div className="space-y-2">
            {spec.params.map((p) => (
              <label key={p.index} className="flex items-center gap-3">
                <span className="w-20 shrink-0 font-mono text-sm text-gray-600">{p.placeholder}</span>
                <input
                  value={values[String(p.index)] || ""}
                  onChange={(e) => setValues((v) => ({ ...v, [String(p.index)]: e.target.value }))}
                  placeholder={`Default for parameter ${p.index}`}
                  className="min-h-[44px] w-full rounded-xl border border-gray-300 px-3"
                />
              </label>
            ))}
          </div>
        </>
      )}

      {spec.body_text && (
        <div className="rounded-xl border border-gray-200 p-3">
          <div className="mb-1 text-xs uppercase text-gray-500">Preview</div>
          <div className="whitespace-pre-wrap text-sm text-gray-800">{rendered}</div>
          {spec.footer_text && (
            <div className="mt-2 text-xs text-gray-400">{spec.footer_text}</div>
          )}
        </div>
      )}

      {spec.param_count > 0 && (
        <button onClick={save} disabled={saving}
          className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl bg-[#F96168] px-4 font-semibold text-black disabled:opacity-50">
          <FiSave /> {saving ? "Saving…" : "Save default parameters"}
        </button>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   Recipients
   ═════════════════════════════════════════════════════════════════════════ */
function Recipients({ campaign, onChanged, onError, onOk }) {
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("replace");
  const [busy, setBusy] = useState(false);
  const [list, setList] = useState({ total: 0, recipients: [] });
  const [warnings, setWarnings] = useState([]);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setList(await call(`/campaigns/${campaign.campaign_id}/recipients?limit=25`));
    } catch (e) { onError(e.message); }
  }, [campaign.campaign_id, onError]);

  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    setWarnings([]);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", mode);
      fd.append("language_code", campaign.language_code || "en_US");
      const out = await call(`/campaigns/${campaign.campaign_id}/recipients`,
                             { method: "POST", body: fd });
      onOk(
        `${out.inserted} recipient(s) added` +
        (out.triggered_run_id ? " — immediate run queued." : ".")
      );
      setWarnings(out.warnings || []);
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
      await load();
      onChanged();
    } catch (e) { onError(e.message); } finally { setBusy(false); }
  };

  const clear = async () => {
    if (!window.confirm(`Remove all ${list.total} recipients from this campaign?`)) return;
    setBusy(true);
    try {
      await call(`/campaigns/${campaign.campaign_id}/recipients`, { method: "DELETE" });
      onOk("Recipient list cleared.");
      setWarnings([]);
      await load();
      onChanged();
    } catch (e) { onError(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-dashed border-gray-300 p-4">
        <input ref={inputRef} type="file" accept=".csv,.xlsx,.xlsm,text/csv"
               onChange={(e) => setFile(e.target.files?.[0] || null)}
               className="w-full text-sm" />
        <p className="mt-2 text-xs text-gray-500">
          CSV or XLSX. One column must be the phone number (<code>phonenumber</code>, <code>phone</code>,
          <code> mobile</code>, <code>msisdn</code>…). Optional <code>Param 1</code>, <code>Param 2</code>…
          columns fill the template variables, and <code>name</code> is kept for reference.
        </p>
        <a href={`${API}/downloads/campaigns_phnos.xlsx`}
           className="mt-2 inline-block text-xs font-semibold text-[#F96168] underline">
          Download a sample file
        </a>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {[
          { v: "replace", l: "Replace list", d: "Clear then upload" },
          { v: "append", l: "Add to list", d: "Keep existing, skip duplicates" },
        ].map((o) => (
          <button key={o.v} onClick={() => setMode(o.v)}
            className={`rounded-xl border px-3 py-2.5 text-left transition ${
              mode === o.v ? "border-[#F96168] bg-[#F96168]/10 ring-1 ring-[#F96168]"
                           : "border-gray-200 hover:bg-gray-50"}`}>
            <div className="text-sm font-semibold text-gray-900">{o.l}</div>
            <div className="text-xs text-gray-500">{o.d}</div>
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-2 sm:flex-row">
        <button onClick={upload} disabled={!file || busy}
          className="flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-xl bg-[#F96168] px-4 font-semibold text-black disabled:opacity-50">
          <FiUpload /> {busy ? "Uploading…" : "Upload recipients"}
        </button>
        {list.total > 0 && (
          <button onClick={clear} disabled={busy}
            className="flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-red-200 px-4 font-semibold text-red-600 disabled:opacity-50">
            <FiTrash2 /> Clear
          </button>
        )}
      </div>

      {warnings.length > 0 && (
        <Banner kind="warn">
          <ul className="list-disc space-y-1 pl-4">
            {warnings.slice(0, 8).map((w, i) => <li key={i}>{w}</li>)}
            {warnings.length > 8 && <li>…and {warnings.length - 8} more.</li>}
          </ul>
        </Banner>
      )}

      <div>
        <div className="mb-2 flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-700">
            <FiUsers className="mr-1 inline" /> {list.total} recipient(s)
          </span>
          <button onClick={load} className="text-sm text-[#F96168]">Refresh</button>
        </div>
        {list.recipients.length > 0 && (
          <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
            <table className="min-w-[420px] w-full text-sm">
              <thead className="text-left text-xs uppercase text-gray-500">
                <tr><th className="py-2">Phone</th><th>Parameters</th><th>Status</th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {list.recipients.map((r) => (
                  <tr key={r.id}>
                    <td className="py-2 font-mono text-xs">{r.phonenumber}</td>
                    <td className="py-2 text-xs text-gray-600">
                      {Object.keys(r.params || {}).length
                        ? Object.entries(r.params)
                            .sort((a, b) => Number(a[0]) - Number(b[0]))
                            .map(([k, v]) => `{{${k}}}=${v}`).join(", ")
                        : "—"}
                    </td>
                    <td className="py-2 text-xs">{r.status || "queued"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {list.total > list.recipients.length && (
              <p className="mt-2 text-xs text-gray-500">
                Showing {list.recipients.length} of {list.total}.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   Run history, with per-run drill-down
   ═════════════════════════════════════════════════════════════════════════ */
function RunSends({ campaign, runId, onError }) {
  const [sends, setSends] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const out = await call(`/campaigns/${campaign.campaign_id}/runs/${runId}/sends?limit=200`);
        if (alive) setSends(out.sends || []);
      } catch (e) { if (alive) onError(e.message); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [campaign.campaign_id, runId, onError]);

  if (loading) return <p className="py-2 text-xs text-gray-500">Loading messages…</p>;
  if (!sends.length) return <p className="py-2 text-xs text-gray-500">No messages recorded for this run.</p>;

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-100 bg-gray-50 p-2">
      <table className="min-w-[520px] w-full text-xs">
        <thead className="text-left uppercase text-gray-500">
          <tr><th className="py-1">Phone</th><th>Status</th><th>Attempts</th><th>Detail</th></tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {sends.map((s) => (
            <tr key={s.id}>
              <td className="py-1 font-mono">{s.phonenumber}</td>
              <td className="py-1">{s.status}</td>
              <td className="py-1">{s.attempts}</td>
              <td className="py-1 max-w-[280px] truncate text-gray-500" title={s.error || s.message_id || ""}>
                {s.error || s.message_id || "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Runs({ campaign, refreshKey, onError }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openRun, setOpenRun] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const out = await call(`/campaigns/${campaign.campaign_id}/runs?limit=25`);
        if (alive) setRuns(out.runs || []);
      } catch (e) { if (alive) onError(e.message); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [campaign.campaign_id, refreshKey, onError]);

  if (loading) return <p className="text-sm text-gray-500">Loading runs…</p>;
  if (!runs.length) {
    return (
      <p className="text-sm text-gray-500">
        No runs yet. A run appears here once the schedule fires or you press “Run now”.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
        <table className="min-w-[620px] w-full text-sm">
          <thead className="text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="py-2">Started</th><th>Trigger</th><th>Status</th>
              <th className="text-right">Queued</th><th className="text-right">Delivered</th>
              <th className="text-right">Failed</th><th />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {runs.map((r) => (
              <React.Fragment key={r.run_id}>
                <tr>
                  <td className="py-2 whitespace-nowrap">{fmt(r.started_at || r.scheduled_for)}</td>
                  <td className="py-2 text-xs">{r.trigger}</td>
                  <td className="py-2 text-xs">
                    <span className={r.status === "completed" ? "text-green-700"
                      : r.status === "running" ? "text-brand-700"
                      : r.status === "pending" ? "text-amber-700" : "text-gray-600"}>{r.status}</span>
                    {r.error && <span className="ml-1 text-red-600" title={r.error}>!</span>}
                  </td>
                  <td className="py-2 text-right">{r.queued}</td>
                  <td className="py-2 text-right">{r.delivered}</td>
                  <td className="py-2 text-right">{r.failed || 0}</td>
                  <td className="py-2 text-right">
                    <button
                      onClick={() => setOpenRun(openRun === r.run_id ? null : r.run_id)}
                      className="text-xs font-semibold text-[#F96168]">
                      {openRun === r.run_id ? "Hide" : "Messages"}
                    </button>
                  </td>
                </tr>
                {openRun === r.run_id && (
                  <tr>
                    <td colSpan={7} className="pb-3">
                      <RunSends campaign={campaign} runId={r.run_id} onError={onError} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   The manager for one campaign
   ═════════════════════════════════════════════════════════════════════════ */
export function CampaignDetail({ campaignId, onBack, onChanged }) {
  const [campaign, setCampaign] = useState(null);
  const [tab, setTab] = useState("recipients");
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [busy, setBusy] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [loadFailed, setLoadFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      const out = await call(`/campaigns/${campaignId}`);
      setCampaign(out.campaign);
      setLoadFailed(false);
    } catch (e) { setErr(e.message); setLoadFailed(true); }
  }, [campaignId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const bump = () => { setRefreshKey((k) => k + 1); onChanged?.(); };

  const act = async (path, label) => {
    setBusy(true); setErr(""); setOk("");
    try {
      const out = await call(`/campaigns/${campaignId}/${path}`, { method: "POST" });
      setOk(out.message || `${label} done.`);
      if (out.warnings?.length) setErr(out.warnings.join(" "));
      bump();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  if (loadFailed) {
    return (
      <div>
        {onBack && (
          <button onClick={onBack} className="mb-3 inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900">
            <FiChevronLeft /> Back
          </button>
        )}
        <Banner kind="error">Could not load this campaign: {err}</Banner>
        <Diagnostics />
      </div>
    );
  }
  if (!campaign) return <p className="text-sm text-gray-500">Loading campaign…</p>;

  const status = campaign.status;
  const canPause = status === "ACTIVE";
  const canActivate = status === "PAUSED" || status === "DRAFT";
  const scheduled = Boolean(campaign.start_date);
  const recipients = campaign.counts?.recipients ?? 0;

  return (
    <div>
      {onBack && (
        <button onClick={onBack}
                className="mb-3 inline-flex min-h-[40px] items-center gap-1 text-sm text-gray-600 hover:text-gray-900">
          <FiChevronLeft /> All campaigns
        </button>
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="truncate text-xl font-bold text-gray-900 sm:text-2xl">{campaign.campaign_name}</h1>
          <p className="mt-1 text-sm text-gray-600">
            Template <span className="font-mono">{campaign.template_name || "—"}</span> ·{" "}
            {recipients} recipients · {campaign.language_code}
            {campaign.waba_account && (
              <>
                {" · sending from "}
                <span className="font-mono">{campaign.waba_account}</span>
              </>
            )}
          </p>
        </div>
        <Pill status={status} />
      </div>

      {/* what happens next */}
      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <div className="rounded-xl border border-gray-200 bg-white p-3">
          <div className="text-xs uppercase text-gray-500"><FiClock className="mr-1 inline" />Schedule</div>
          <div className="mt-1 text-sm font-semibold text-gray-900">
            {scheduled ? campaign.schedule?.description || "—" : "Not scheduled yet"}
          </div>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-3">
          <div className="text-xs uppercase text-gray-500"><FiCalendar className="mr-1 inline" />Next run</div>
          <div className="mt-1 text-sm font-semibold text-gray-900">
            {status === "PAUSED" ? "Paused" : fmt(campaign.next_run_at)}
          </div>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-3">
          <div className="text-xs uppercase text-gray-500"><FiZap className="mr-1 inline" />Runs so far</div>
          <div className="mt-1 text-sm font-semibold text-gray-900">
            {campaign.run_count} · last {fmt(campaign.last_run_at)}
          </div>
        </div>
      </div>

      {/* controls */}
      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        {canPause && (
          <button onClick={() => act("pause", "Pause")} disabled={busy}
            className="flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 font-semibold text-amber-800 disabled:opacity-50">
            <FiPause /> Pause
          </button>
        )}
        {canActivate && (
          <button onClick={() => act("resume", status === "DRAFT" ? "Activate" : "Resume")}
            disabled={busy || !scheduled}
            title={scheduled ? "" : "Set a schedule first"}
            className="flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-xl border border-green-300 bg-green-50 px-4 font-semibold text-green-800 disabled:opacity-50">
            <FiPlay /> {status === "DRAFT" ? "Activate" : "Resume"}
          </button>
        )}
        <button
          onClick={() => {
            if (window.confirm(
              `Send "${campaign.template_name}" to all ${recipients} recipients now?`
            )) act("run-now", "Run");
          }}
          disabled={busy || !recipients}
          title={recipients ? "" : "Upload recipients first"}
          className="flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-xl bg-[#F96168] px-4 font-semibold text-black disabled:opacity-50">
          <FiZap /> Run now
        </button>
        <button onClick={bump} aria-label="Refresh"
          className="flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-gray-300 px-4 font-semibold text-gray-700">
          <FiRefreshCw />
        </button>
      </div>

      <div className="mt-4 space-y-2">
        <Banner kind="error" onClose={() => setErr("")}>{err}</Banner>
        <Banner kind="ok" onClose={() => setOk("")}>{ok}</Banner>
        {status === "PAUSED" && (
          <Banner kind="warn">
            Paused — nothing new is queued. Messages already handed to the sender will still go out.
          </Banner>
        )}
        {status === "DRAFT" && (
          <Banner kind="info">
            Draft. {scheduled
              ? "Upload recipients and press Activate to put it on the schedule."
              : "Set a schedule on the Schedule tab, then activate it."}
            {" "}“Run now” works regardless, and sends once.
          </Banner>
        )}
      </div>

      {/* tabs */}
      <div className="-mx-4 mt-5 overflow-x-auto px-4 sm:mx-0 sm:px-0">
        <div className="flex w-max gap-2 rounded-2xl border border-gray-200 bg-gray-100 p-1.5 sm:w-fit">
          {[
            { id: "recipients", l: "Recipients", icon: FiUsers },
            { id: "params", l: "Parameters", icon: FiSliders },
            { id: "schedule", l: "Schedule", icon: FiClock },
            { id: "runs", l: "Run history", icon: FiActivity },
          ].map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 whitespace-nowrap rounded-xl px-4 py-2.5 text-sm font-semibold ${
                tab === t.id ? "bg-white text-[#D1272F] shadow-sm" : "text-gray-600"}`}>
              <t.icon /> {t.l}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 rounded-2xl border border-gray-200 bg-white p-4 sm:p-6">
        {tab === "recipients" && (
          <Recipients campaign={campaign} onError={setErr} onOk={setOk} onChanged={bump} />
        )}
        {tab === "params" && (
          <TemplateParams campaign={campaign} onError={setErr}
            onSaved={() => { setOk("Default parameters saved."); bump(); }} />
        )}
        {tab === "schedule" && (
          <ScheduleEditor campaign={campaign} onError={setErr}
            onSaved={(out) => { setOk(out.message || "Schedule saved."); bump(); }} />
        )}
        {tab === "runs" && <Runs campaign={campaign} refreshKey={refreshKey} onError={setErr} />}
      </div>
    </div>
  );
}

